import styled, { css } from 'styled-components'
import { Box } from '../../../shared_components/UI/Box'
import {
  SeasonVideoBanner,
  TimeUntilSeasonEnd,
  TimeUntilDayEnd,
} from './About'
import { H2, Span } from '../../../shared_components/Library/Typeography'
import { useHistory, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  selectCurrentSeason,
  selectCurrentSeasonProgress,
} from '../../../modules/seasons/selectors'
import { Card } from '../../../shared_components/Library/Card'
import { IQuest, QuestProgress } from '../../../modules/seasons/slice'
import { Seperator } from '../../Memecoins/TokenPage/Threads/ThreadList'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import { useEffect, useMemo, useState } from 'react'
import ProgressBar from '../../../shared_components/Library/ProgressBar'
import { improvedAmountFromWeiFloat } from '../../../contexts/StakingContext'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import TickUpNumber from '../../../shared_components/TickUpNumber'
import { overlayPageCss, useShouldShowForThreshold } from '../../../theme'
import SeasonToggler from './Toggler'
import { selectAccount } from '../../../modules/web3/selectors'

export type QuestWithProgress = IQuest &
  QuestProgress & { oldProgress?: string }

export default function Progress() {
  const account = useSelector(selectAccount)
  const history = useHistory()
  const location = useLocation()
  const isAtLeastMediumDisplaySize = useShouldShowForThreshold('medium')
  const handleAboutClick = () => {
    const searchParams = new URLSearchParams(location.search)
    searchParams.set('modal', 'about')
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    })
  }
  const currentSeason = useSelector(selectCurrentSeason)
  const currentSeasonProgress = useSelector(selectCurrentSeasonProgress)

  if (!currentSeason || !currentSeasonProgress) {
    return <AnimatedLoader onlyWhite />
  }

  const {
    meta_data: { quests },
  } = currentSeason

  const [dailyQuests, seasonalQuests]: QuestWithProgress[][] = quests.reduce(
    (acc: QuestWithProgress[][], quest: QuestWithProgress) => {
      const progressForIsQuest = currentSeasonProgress.quests.find(
        (progress) => progress.questId === quest.questId,
      )
      acc[quest.duration === 'daily' ? 0 : 1].push({
        ...quest,
        ...progressForIsQuest,
      })
      return acc
    },
    [[], []],
  )

  return (
    <Wrapper>
      <Box dc g={32} padT={isAtLeastMediumDisplaySize ? 32 : 0}>
        {isAtLeastMediumDisplaySize && <SeasonToggler />}
        <SeasonVideoBanner smallOnDesktop />
      </Box>
      <PageBody>
        <SeasonalQuestsWrapper>
          <Box jsb ac>
            <PseudoLink ac g={4} onClick={handleAboutClick}>
              <img src="/img/icons/info-circle-black.svg" alt="info icon" />
              <AboutLink>About</AboutLink>
            </PseudoLink>
            <TimeUntilSeasonEnd />
          </Box>
          <QuestsContainer>
            {seasonalQuests.map((quest, i) => (
              <>
                <QuestWithProgressCard quest={quest} key={quest.questId + account} />
                {i < seasonalQuests.length - 1 && <Seperator />}
              </>
            ))}
          </QuestsContainer>
        </SeasonalQuestsWrapper>
        <DailyQuestsWrapper>
          <Box dc g={12}>
            <Box ac jsb>
              <H2 s="2XL" fw="semibold" c="text-primary">
                Daily Quests
              </H2>
              <TimeUntilDayEnd />
            </Box>
            <Span s="M" c="text-primary" style={{ display: 'inline-flex' }}>
              Earn 1x{' '}
              <img
                src="/img/icons/raffle-ticket.svg"
                alt="raffle ticket"
                style={{
                  width: 16,
                  height: 16,
                  marginLeft: 4,
                  marginRight: 4,
                  marginTop: 1,
                }}
              />
              for completing all your daily challenges
            </Span>
          </Box>
          <QuestsContainer>
            {dailyQuests.map((quest, i) => (
              <>
                <QuestWithProgressCard quest={quest} key={quest.questId + account} />
                {i < dailyQuests.length - 1 && <Seperator />}
              </>
            ))}
          </QuestsContainer>
        </DailyQuestsWrapper>
      </PageBody>
    </Wrapper>
  )
}

const DailyQuestsWrapper = styled(Box)`
  flex-direction: column;
  padding: 24px var(--Index-Grids-Gutters, 16px);
  gap: 16px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
  }
`

export function QuestWithProgressCard({
  quest,
  isCompact,
}: {
  quest: QuestWithProgress
  isCompact?: boolean
  borderless?: boolean
}) {
  const {
    name,
    progress,
    progressRequirement,
    questType,
    duration,
    lastUpdated,
    maxTickets,
    oldProgress,
  } = quest

  const isDailyQuest = duration === 'daily'
  const lastUpdatedDate = !!lastUpdated
    ? new Date(lastUpdated).toISOString().split('T')[0]
    : new Date(0).toISOString().split('T')[0]
  const todayDate = new Date().toISOString().split('T')[0]
  const anyProgressToday = lastUpdatedDate === todayDate
  const isWeiProgress = progressRequirement.length > 5 // bit rogue but it works
  const isLinear = questType === 'linear'

  const { partialCompletionTickets: partialTicketsEarned, timesCompleted } =
    quest

  const ticketsEarned = isLinear
    ? timesCompleted > 0
      ? maxTickets
      : partialTicketsEarned
    : maxTickets

  const hasOldProgress = typeof oldProgress !== 'undefined'

  const [progressPercentage, setProgressPercentage] = useState(
    !anyProgressToday && isDailyQuest
      ? 0
      : Number(
        Number(hasOldProgress ? oldProgress : progress) /
        Number(progressRequirement),
      ) * 100,
  )

  const ANIMATE_DELAY = 600

  useEffect(() => {
    if (!hasOldProgress) return
    const timeout = setTimeout(() => {
      setProgressPercentage(
        Math.min(100, Number(progress) / Number(progressRequirement)) * 100,
      )
    }, ANIMATE_DELAY)

    return () => clearTimeout(timeout)
  }, [progress, progressRequirement, oldProgress, hasOldProgress])

  const progressText = useMemo(() => {
    const progressText =
      !anyProgressToday && isDailyQuest
        ? 0
        : isWeiProgress
          ? showDecimalsIfNeeded(
            improvedAmountFromWeiFloat(progress),
            4,
          ).toString()
          : progress

    const progressRequirementText = isWeiProgress
      ? improvedAmountFromWeiFloat(progressRequirement).toString()
      : progressRequirement

    if (isLinear) {
      return (
        <Span fw="semibold" c="text-primary" s="M">
          {Math.min(progressPercentage, 100).toFixed(2)}%
        </Span>
      )
    }
    return (
      <Span c="text-primary" s="M">
        <Span
          fw="semibold"
          color={isCompact ? 'text-brandPrimary' : 'text-primary'}
        >
          {isCompact ? (
            <TickUpNumber amount={Number(progressText)} />
          ) : (
            progressText
          )}
        </Span>{' '}
        <Span c="text-secondary">of {progressRequirementText}</Span>
      </Span>
    )
  }, [
    progress,
    progressRequirement,
    isLinear,
    progressPercentage,
    isWeiProgress,
    anyProgressToday,
    isDailyQuest,
  ])

  const RightItem = () => (
    <TicketsRightChip
      g={4}
      hasCompleted={progressPercentage >= 100}
      leftHeavy={isLinear}
    >
      {!isLinear && (
        <img src="/img/icons/raffle-ticket.svg" alt="raffle ticket" />
      )}
      {isLinear && !!ticketsEarned && (
        <Span s="M" fw="regular">
          <Span s="L" fw="semibold">
            {ticketsEarned}
          </Span>{' '}
          of
        </Span>
      )}
      <Span s={isLinear ? 'M' : 'L'} fw={isLinear ? 'regular' : 'semibold'}>
        {maxTickets}
      </Span>
      {isLinear && (
        <img src="/img/icons/raffle-ticket.svg" alt="raffle ticket" />
      )}
    </TicketsRightChip>
  )

  return (
    <Box dc g={isCompact ? 12 : 16}>
      <Box ac jsb>
        <Span s="L" fw="semibold" c="text-primary">
          {name}
        </Span>
        {progressText}
      </Box>
      <ProgressBar
        pctProgress={Math.min(
          Number(progressPercentage),
          100 - Number(String(maxTickets).length * 5),
        )}
        h={18}
        RightItem={RightItem}
      />
    </Box>
  )
}

const TicketsRightChip = styled(Box) <{
  hasCompleted: boolean
  leftHeavy: boolean
}>`
  border-radius: var(--units-88, 88px);
  border: 1px solid
    ${({ theme, hasCompleted }) =>
    hasCompleted
      ? theme.semantics.zap.stroke.brand
      : 'var(--stroke-secondary, #333)'};
  background: var(--surface-primary, #080808);
  backdrop-filter: blur(40px);
  position: absolute;
  right: -4px;
  display: flex;
  padding: 6px ${({ leftHeavy }) => (leftHeavy ? 6 : 10)}px 6px
    ${({ leftHeavy }) => (!leftHeavy ? 6 : 10)}px;
  align-items: center;
  gap: 4px;
  width: auto;
  flex: unset;
  height: 32px;
  img {
    width: 20px;
    height: 20px;
  }
`

export const Wrapper = styled(Box)`
  flex-direction: column;
  video {
    position: relative;
    @media screen and (min-width: ${({ theme }) =>
    theme.breakpoints.medium}px) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      width: 100%;
    }
  }
  ${Seperator} {
    margin: 0 !important;
    opacity: 1;
    background: var(--stroke-tertiary, #252525);
  }
  ${overlayPageCss}
`

export const PageBody = styled(Box)`
  flex-direction: column;
  gap: var(--Index-Grids-Margins);
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    gap: 32px;
  }
`

const PseudoLink = styled(Box)`
  cursor: pointer;
`

const AboutLink = styled(Span)`
  color: var(--text-invert, #080808);
  text-align: right;
  font-family: var(--type-family-space-grotesk, 'Space Grotesk');
  font-size: var(--Text-L-L-size);
  font-style: normal;
  font-weight: var(--type-weight-Semibold);
  line-height: var(--Text-L-L-line-height); /* 125% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
`

const SeasonalQuestsWrapper = styled(Box)`
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  padding: var(--Index-Grids-Gutters, 16px);
  gap: 16px;
  flex-direction: column;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    border-bottom-left-radius: var(--units-12, 12px);
    border-bottom-right-radius: var(--units-12, 12px);
  }
`

export const QuestsContainer = styled(Card)`
  border-radius: var(--units-12, 12px);
  border: 1px solid var(--stroke-secondary, #333);
  background: var(--surface-primary, #080808);
  gap: 16px;
`

export const TopVideoPostbox = styled(Box) <{ smallOnDesktop?: boolean }>`
  ${({ smallOnDesktop }) =>
    smallOnDesktop &&
    css`
      @media screen and (min-width: ${({ theme }) =>
        theme.breakpoints.medium}px) {
        border-top-left-radius: var(--units-12, 12px);
        border-top-right-radius: var(--units-12, 12px);
        max-height: 160px;
        overflow: hidden;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }
    `}
`
